import {
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
} from "@tanstack/react-query";
import {
  cancelInvoice,
  createFreeInvoice,
  createInvoice,
  getFiscalInvoiceAvailability,
  getInvoiceById,
  listInvoices,
  markInvoiceOriginalPrinted,
  updateInvoiceCustomer,
  type FreeInvoicePayload,
  type InvoiceCreatePayload,
  type InvoiceCustomerPayload,
  type InvoiceListFilters,
} from "../api/invoices";

export const useInvoicesList = (filters: InvoiceListFilters) => {
  return useQuery({
    queryKey: ["invoices", "list", filters],
    queryFn: () => listInvoices(filters),
    staleTime: 1000 * 60,
  });
};

// „Фактуриране" → може ли закриването да издаде фискална фактура. Под
// „invoices" — всяко издаване (и закриване с фискална фактура) инвалидира
// префикса и „следващ номер" се опреснява сам. Без повторение: паднало питане
// се казва веднага в избора (`useInvoicingFlow`). По-старо BE без адреса не
// стига дотук — питането тръгва само за фирма в `fiscal`/`both`, а режим има
// само новото BE.
export const useFiscalInvoiceAvailability = (options?: { enabled?: boolean }) => {
  return useQuery({
    queryKey: ["invoices", "fiscal-availability"],
    queryFn: getFiscalInvoiceAvailability,
    staleTime: 0,
    retry: false,
    enabled: options?.enabled ?? true,
  });
};

export const useInvoiceById = (id: number | null) => {
  return useQuery({
    queryKey: ["invoices", "detail", id],
    queryFn: () => getInvoiceById(id!),
    enabled: id != null,
    staleTime: 1000 * 60 * 5,
  });
};

const invalidateAfterIssue = (qc: QueryClient) => {
  // Инвалидираме списъка с нефактурирани сметки — те вече не са кандидати.
  qc.invalidateQueries({ queryKey: ["bills", "uninvoiced"] });
  // ["companies"] е по префикс → обхваща и текущата фирма
  // (["companies","default"], свеж last_invoice_number) и „exists". Втора
  // инвалидация само на "default" отменяше тази и пращаше втори GET.
  qc.invalidateQueries({ queryKey: ["companies"] });
  qc.invalidateQueries({ queryKey: ["customers"] });
  // Новата фактура трябва да се вижда в справката „Фактури" веднага —
  // списъкът е със staleTime 60s и без инвалидация мълчи до минута.
  qc.invalidateQueries({ queryKey: ["invoices"] });
};

export const useCreateInvoice = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: InvoiceCreatePayload) => createInvoice(data),
    onSuccess: (_invoice, data) => {
      invalidateAfterIssue(qc);
      // Фактура за депозит — номерът ѝ се вижда в „Справка депозити".
      if (data.deposit_id) {
        qc.invalidateQueries({ queryKey: ["deposits"] });
        qc.invalidateQueries({ queryKey: ["reports", "deposits"] });
      }
    },
  });
};

// Свободна фактура — бон от редовете (ако методът се фискализира), после
// фактурата. Отговор без фактура (неиздаден бон) не мени нищо в базата, но
// инвалидацията е безвредна и пази списъците верни и при частичен изход.
export const useCreateFreeInvoice = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: FreeInvoicePayload) => createFreeInvoice(data),
    onSuccess: () => invalidateAfterIssue(qc),
  });
};

export const useUpdateInvoiceCustomer = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: InvoiceCustomerPayload }) =>
      updateInvoiceCustomer(id, data),
    onSuccess: () => {
      // Списъкът и детайлът показват името/ЕИК-а на получателя — опресняваме
      // ги; preview-то чете от ["invoices", "detail", id] и се обновява само.
      // Клиентът се upsert-ва в регистъра (както при издаване) → и него.
      qc.invalidateQueries({ queryKey: ["invoices"] });
      qc.invalidateQueries({ queryKey: ["customers"] });
    },
  });
};

export const useCancelInvoice = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => cancelInvoice(id),
    onSuccess: () => {
      // Списъкът с фактури + нефактурираните сметки трябва да се опреснят —
      // анулираната фактура „освобождава" сметките си за повторно фактуриране.
      qc.invalidateQueries({ queryKey: ["invoices"] });
      qc.invalidateQueries({ queryKey: ["bills", "uninvoiced"] });
    },
  });
};

// A4 печатът на оригинала — прегледът в справките чете фактурата от
// `["invoices","detail",id]`, затова отговорът отива направо там: следващият
// печат е дубликат без нова заявка.
export const useMarkInvoiceOriginalPrinted = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => markInvoiceOriginalPrinted(id),
    onSuccess: (invoice) => {
      qc.setQueryData(["invoices", "detail", invoice.id], invoice);
    },
  });
};
